import {Occupancy, OccupyRequest} from "@variocube/app-api";
import {
    BoxStorage,
    MessageView,
    PendingView,
    PushButton,
    useAsync,
    useBoxes,
    usePlatform,
    usePreventDeactivate
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {auditLog} from "./auditLog";
import {logger} from "./logger";
import {MessageKey, useLocalization} from "../i18n";

const log = logger.sub("StorageView");

type StorageViewProps = {
    request: OccupyRequest;
    onWrongSize: () => void;
    onCancel: () => void;
    onPreConfirm?: (occupancy: Occupancy) => Promise<void>;
    onConfirm: (occupancy: Occupancy) => Promise<void>;
    boxOpenTitle: MessageKey;
    boxOpenMessage: MessageKey;
    confirmTitle: MessageKey;
    confirmMessage: MessageKey;
}

export function StorageView(props: StorageViewProps) {
    const {
        request,
        boxOpenTitle,
        boxOpenMessage,
        confirmTitle,
        confirmMessage,
        onPreConfirm,
        onConfirm,
        onCancel,
        onWrongSize
    } = props;

    const platform = usePlatform();
    const {t} = useLocalization();
    const allBoxes = useBoxes();

    const [occupancy, setOccupancy] = useState<Occupancy>();

    const [occupanciesPending, setOccupanciesPending] = useState<Occupancy[]>([]);

    usePreventDeactivate();

    const {loading, error} = useAsync(async () => {
        if (!occupancy) {
            log.info("Occupying box", request);
            setOccupancy(await platform.occupy(request));
        }
    }, [platform, request, occupancy]);

    const box = useMemo(() => allBoxes.find(box => box.number == occupancy?.boxNumber), [allBoxes, occupancy]);

    const handleConfirm = useCallback(async () => { 
        if (occupancy) {
            if (onPreConfirm) {
                await onPreConfirm(occupancy);
            }
            log.info("Confirming occupancy", occupancy);
            await platform.confirmOccupancy(occupancy.uuid);
            await onConfirm(occupancy);
        }
        await cancelAllPendingOccupancies();
    }, [occupancy, onConfirm, onPreConfirm, platform]);

    const handleWrongSize = useCallback(async () => {
        log.info("User selected wrong size.")
        if (occupancy) {
            if (occupanciesPending)
                occupanciesPending.push(occupancy);
            setOccupancy(undefined);
            onWrongSize();
        }
    }, [occupancy, platform, onWrongSize]);

    const handleCancel = useCallback(async () => {
        log.info("User canceled.");
        if (occupancy) {
            await platform.cancelOccupancy(occupancy.uuid);
        }
        onCancel();
        await cancelAllPendingOccupancies();
    }, [occupancy, platform, onCancel]);

    const handleMaintenance = useCallback(async () => {
        log.info("User requested maintenance.");
        if (occupancy) {
            try {
                await platform.requireBoxMaintenance(occupancy.boxNumber);
                await platform.cancelOccupancy(occupancy.uuid);
                await auditLog(platform, "Maintenance", {boxNumber: occupancy.boxNumber});
            } catch (error) {
                log.error("Error while handling maintenance", error);
            }
        }
        setOccupancy(undefined);
        await cancelAllPendingOccupancies();
    }, [occupancy, platform]);

    async function cancelAllPendingOccupancies() { 
        for (const occupancy of occupanciesPending) { 
          log.debug("cancel pending occupany", occupancy); 
          await platform.cancelOccupancy(occupancy.uuid); 
        }
        setOccupanciesPending([]);
      }

    if (loading) {
        return <PendingView message={t("OccupancyPending")}/>;
    }
    else if (occupancy && box) {
        return (
            <BoxStorage
                platform={platform}
                boxes={box}
                onConfirm={handleConfirm}
                strings={t}
                boxOpenTitle={t(boxOpenTitle)}
                boxOpenMessage={t(boxOpenMessage, {boxNames: box.number})}
                confirmTitle={t(confirmTitle)}
                confirmMessage={t(confirmMessage, {boxNames: box.number})}
                onWrongSize={handleWrongSize}
                onMaintenance={handleMaintenance}
                onCancel={handleCancel}
                timeout={true}
                onTimeout={handleCancel}
                allowCancelWhenOpen={true}
            />
        );
    }
    else if (error) {
        return (
            <MessageView message={t("BoxTypeNotAvailable")}>
                <PushButton onClick={handleCancel}>
                    {t("Cancel")}
                </PushButton>
                <PushButton primary onClick={onWrongSize}>
                    {t("SelectOtherSize")}
                </PushButton>
            </MessageView>
        );
    }
    return null;
}
